<template>
    <div class="bb-scroll" lang="el">
        <!-- a cup with no bracket made in the CMS -->
        <div v-if="!bracket" class="bb bb--empty">
            <svg class="bb-empty__cup" width="48" height="48" viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.trophy" /></svg>
            <div>{{ emptyText }}</div>
        </div>
        <div v-else ref="board" :class="['bb', { 'bb--narrow': narrow }]" :style="{ '--bb-card-w': `${cardWidth}px` }">
            <div class="bb-head">
                <svg class="bb-head__ball" width="34" height="34" viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.ball" /></svg>
                <div>
                    <div class="bb-head__title">{{ title }}</div>
                    <div v-if="subtitle" class="bb-head__subtitle">{{ subtitle }}</div>
                </div>
            </div>
            <div v-if="bracket.automatic" class="bb-note">
                {{ bracket.provisionalSeeds
                    ? 'Playoffs picture: τα ζευγάρια αν τα playoffs ξεκινούσαν με τη σημερινή βαθμολογία.'
                    : 'Playoffs picture: τα ζευγάρια από τη βαθμολογία της κανονικής περιόδου.' }}
            </div>
            <div v-else-if="bracket.provisionalSeeds" class="bb-note">
                Η κανονική περίοδος δεν έχει ολοκληρωθεί: τα seeds είναι προσωρινά.
            </div>

            <!-- phones: one round at a time, the champion on top -->
            <template v-if="narrow">
                <div class="bb-champion bb-champion--static">
                    <svg :class="['bb-champion__cup', { 'bb-champion__cup--won': bracket.champion }]" width="48" height="48" viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.trophy" /></svg>
                    <div class="bb-champion__label">{{ bracket.cup ? 'Κυπελλούχος' : 'Πρωταθλητής' }}</div>
                    <div class="bb-champion__name">{{ bracket.champion ? bracket.champion.name : '—' }}</div>
                </div>
                <div class="bb-tabs">
                    <button
                        v-for="(round, index) in rounds"
                        :key="index"
                        type="button"
                        :class="['bb-tab', { 'bb-tab--active': index === shownRound }]"
                        @click="selectedRound = index"
                    >
                        {{ round.name }}
                    </button>
                </div>
            </template>

            <div class="bb-grid">
                <div
                    v-for="column in columns"
                    :key="column.key"
                    :class="['bb-col', `bb-col--${column.side}`]"
                >
                    <div v-if="column.side !== 'list'" class="bb-col__head">
                        <div class="bb-col__name">{{ column.round.name }}</div>
                        <div class="bb-col__format">{{ formatLabel(column.round) }}</div>
                    </div>
                    <div v-else class="bb-col__format bb-col__format--list">{{ formatLabel(column.round) }}</div>
                    <div class="bb-col__body" :style="column.side === 'list' ? {} : { height: `${boardHeight}px` }">
                        <div
                            v-for="(pair, index) in column.pairs"
                            :key="pair.code"
                            class="bb-cell"
                        >
                            <!-- the lines to the next round and from the previous one -->
                            <span v-if="column.side === 'left' || column.side === 'right'" class="bb-ln bb-ln--out" />
                            <span
                                v-if="(column.side === 'left' || column.side === 'right') && column.pairs.length > 1"
                                :class="['bb-ln', 'bb-ln--join', index % 2 ? 'bb-ln--join-bottom' : 'bb-ln--join-top']"
                            />
                            <span v-if="column.depth > 0 && column.side !== 'list'" class="bb-ln bb-ln--in" />
                            <span v-if="column.side === 'final' && columns.length > 1" class="bb-ln bb-ln--in bb-ln--in-right" />

                            <div v-if="column.side === 'final'" class="bb-champion">
                                <svg :class="['bb-champion__cup', { 'bb-champion__cup--won': bracket.champion }]" width="64" height="64" viewBox="0 0 24 24" aria-hidden="true"><path :d="icons.trophy" /></svg>
                                <div class="bb-champion__label">{{ bracket.cup ? 'Κυπελλούχος' : 'Πρωταθλητής' }}</div>
                                <div class="bb-champion__name">{{ bracket.champion ? bracket.champion.name : '—' }}</div>
                            </div>

                            <div :class="['bb-card', { 'bb-card--bye': pair.bye, 'bb-card--final': column.side === 'final' }]">
                                <div
                                    v-for="key in ['home', 'away']"
                                    :key="key"
                                    :class="['bb-team', {
                                        'bb-team--won': pair.winner === key,
                                        'bb-team--lost': pair.winner && pair.winner !== key,
                                        'bb-team--empty': !pair[key].team
                                    }]"
                                >
                                    <span class="bb-team__seed">{{ pair[key].seed || '' }}</span>
                                    <span class="bb-team__logo" :style="logoStyle(pair[key].team)">
                                        <img
                                            v-if="logos && pair[key].team && pair[key].team.logo"
                                            :src="`https://storage.googleapis.com/${pair[key].team.logo}`"
                                            :alt="pair[key].team.name"
                                        >
                                        <template v-else-if="pair[key].team">{{ initial(pair[key].team.name) }}</template>
                                    </span>
                                    <span class="bb-team__name" :title="pair[key].team ? pair[key].team.name : pair[key].placeholder">
                                        {{ pair[key].team ? pair[key].team.name : placeholderLabel(pair[key].placeholder) }}
                                    </span>
                                    <span v-if="pair[key].advantage" class="bb-team__adv" title="Ξεκινά τη σειρά με μία νίκη">+1</span>
                                    <span class="bb-team__score">{{ score(pair, column.round, key) }}</span>
                                </div>
                                <!-- the picture of the standings has no games -->
                                <div v-if="!bracket.automatic" class="bb-card__games">
                                    <template v-if="pair.bye">Πρόκριση χωρίς αγώνα</template>
                                    <template v-else>
                                        <component
                                            :is="linkMatches ? 'button' : 'span'"
                                            v-for="game in pair.games"
                                            :key="game.matchId"
                                            :class="['bb-game', { 'bb-game--link': linkMatches }]"
                                            :type="linkMatches ? 'button' : null"
                                            @click="linkMatches && $emit('match', game.matchId)"
                                        >
                                            {{ game.played ? `${game.homeScore}-${game.awayScore}` : gameDate(game.date) }}
                                        </component>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // the card and the room around it: every first round pair gets one slot of the board's height
    const CARD_HEIGHT = 84
    const SLOT = CARD_HEIGHT + 30
    const MIN_HEIGHT = 380
    // inline (Material Design Icons paths): an icon font would not be drawn in the image of the CMS
    const ICONS = {
        trophy: 'M18 2C17.1 2 16 3 16 4H8C8 3 6.9 2 6 2H2V11C2 12 3 13 4 13H6.2C6.6 15 7.9 16.7 11 17V19.08C8 19.54 8 22 8 22H16C16 22 16 19.54 13 19.08V17C16.1 16.7 17.4 15 17.8 13H20C21 13 22 12 22 11V2H18M6 11H4V4H6V11M20 11H18V4H20V11Z',
        ball: 'M2.34,14.63C2.94,14.41 3.56,14.3 4.22,14.3C5.56,14.3 6.73,14.72 7.73,15.56L4.59,18.7C3.53,17.5 2.78,16.13 2.34,14.63M15.56,9.8C17.53,11.27 19.66,11.63 21.94,10.88C21.97,11.09 22,11.47 22,12C22,13.03 21.75,14.18 21.28,15.45C20.81,16.71 20.23,17.73 19.55,18.5L13.22,12.19L15.56,9.8M8.77,16.64C9.83,18.17 10.05,19.84 9.42,21.66C8,21.25 6.73,20.61 5.67,19.73L8.77,16.64M12.19,13.22L18.5,19.55C16.33,21.45 13.78,22.25 10.88,21.94C11.09,21.28 11.2,20.56 11.2,19.78C11.2,19.16 11.06,18.43 10.78,17.6C10.5,16.77 10.17,16.09 9.8,15.56L12.19,13.22M8.81,14.5C7.88,13.67 6.8,13.15 5.58,12.91C4.36,12.68 3.19,12.75 2.06,13.13C2.03,12.91 2,12.53 2,12C2,10.97 2.25,9.82 2.72,8.55C3.19,7.29 3.77,6.27 4.45,5.5L11.11,12.19L8.81,14.5M15.56,7.73C14.22,6.08 13.91,4.28 14.63,2.34C15.25,2.5 15.96,2.8 16.76,3.26C17.55,3.71 18.2,4.16 18.7,4.59L15.56,7.73M21.66,9.38C21.06,9.59 20.44,9.7 19.78,9.7C18.69,9.7 17.64,9.38 16.64,8.72L19.73,5.67C20.61,6.77 21.25,8 21.66,9.38M12.19,11.11L5.5,4.45C7.67,2.55 10.22,1.75 13.13,2.06C12.91,2.72 12.8,3.44 12.8,4.22C12.8,4.94 12.96,5.75 13.29,6.66C13.62,7.56 14,8.28 14.5,8.81L12.19,11.11Z'
    }

    export default {
        name: 'BracketBoard',
        props: {
            // the bracket as /brackets returns it; null shows emptyText
            bracket: {
                type: Object,
                default: null
            },
            emptyText: {
                type: String,
                default: 'Δεν έχει οριστεί ακόμα το δέντρο.'
            },
            // the team logos (off for the image of the CMS: the storage does not allow drawing them)
            logos: {
                type: Boolean,
                default: true
            },
            // the scores open the match
            linkMatches: {
                type: Boolean,
                default: true
            },
            cardWidth: {
                type: Number,
                default: 200
            },
            // one round at a time on phones (off for the image of the CMS)
            responsive: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                icons: ICONS,
                narrow: false,
                selectedRound: null,
                media: null
            }
        },
        computed: {
            title() {
                if (!this.bracket) return ''
                if (this.bracket.title) return this.bracket.title
                if (this.bracket.cup) return this.bracket.cupLeagueTitle || 'Κύπελλο'
                return `Playoffs ${this.bracket.leagueTitle || ''}`.trim()
            },
            subtitle() {
                return (this.bracket && this.bracket.seasonName) || ''
            },
            rounds() {
                return (this.bracket && this.bracket.rounds) || []
            },
            // phones: the round picked, else the first one still being played
            shownRound() {
                if (this.selectedRound !== null && this.rounds[this.selectedRound]) return this.selectedRound
                const open = this.rounds.findIndex(round => round.pairs.some(pair => !pair.winner))
                return open === -1 ? Math.max(0, this.rounds.length - 1) : open
            },
            // the earlier rounds split in two halves, left and right, meeting at the final in the middle
            columns() {
                const rounds = this.rounds
                if (!rounds.length) return []
                if (this.narrow) {
                    const round = rounds[this.shownRound]
                    return [{ key: 'list', side: 'list', depth: this.shownRound, round, pairs: round.pairs }]
                }
                const last = rounds.length - 1
                const final = { key: 'final', side: 'final', depth: last, round: rounds[last], pairs: rounds[last].pairs }
                if (last === 0) return [final]
                const left = []
                const right = []
                rounds.slice(0, last).forEach((round, depth) => {
                    const half = round.pairs.length / 2
                    left.push({ key: `l${depth}`, side: 'left', depth, round, pairs: round.pairs.slice(0, half) })
                    right.unshift({ key: `r${depth}`, side: 'right', depth, round, pairs: round.pairs.slice(half) })
                })
                return [...left, final, ...right]
            },
            boardHeight() {
                const firstColumn = this.columns[0]
                const rows = firstColumn ? firstColumn.pairs.length : 1
                return Math.max(MIN_HEIGHT, rows * SLOT)
            }
        },
        watch: {
            bracket() {
                this.selectedRound = null
            }
        },
        mounted() {
            if (!this.responsive || !window.matchMedia) return
            this.media = window.matchMedia('(max-width: 759px)')
            this.onMedia()
            if (this.media.addEventListener) {
                this.media.addEventListener('change', this.onMedia)
            } else {
                this.media.addListener(this.onMedia)
            }
        },
        beforeDestroy() {
            if (!this.media) return
            if (this.media.removeEventListener) {
                this.media.removeEventListener('change', this.onMedia)
            } else {
                this.media.removeListener(this.onMedia)
            }
        },
        methods: {
            onMedia() {
                this.narrow = this.media.matches
            },
            formatLabel(round) {
                // the picture of the standings does not know the format of the series
                if (this.bracket.automatic) return ''
                if (round.bestOf <= 1) return 'Ένας αγώνας'
                return `Best of ${round.bestOf}` + (round.advantage ? ' · αβαντάζ 1-0' : '')
            },
            // a single game shows its score, a series the wins
            score(pair, round, key) {
                if (pair.bye) return ''
                if (round.bestOf <= 1) {
                    const game = pair.games.find(item => item.played)
                    if (!game) return ''
                    return key === 'home' ? game.homeScore : game.awayScore
                }
                if (!pair.home.team || !pair.away.team) return ''
                return key === 'home' ? pair.homeWins : pair.awayWins
            },
            placeholderLabel(text) {
                if (!text) return ''
                return text.replace(/^Νικητής R(\d+)-(\d+)$/, (match, round, index) => {
                    const name = this.rounds[Number(round) - 1] ? this.rounds[Number(round) - 1].name : `Γύρος ${round}`
                    return `Νικητής ${name} ${index}`
                })
            },
            gameDate(date) {
                if (!date) return 'TBD'
                return new Date(date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Athens' })
            },
            initial(name) {
                return String(name || '?').trim().charAt(0).toUpperCase()
            },
            logoStyle(team) {
                if (!team) return {}
                if (this.logos && team.logo) return { background: '#ffffff' }
                return { background: team.color || '#037ade' }
            }
        }
    }
</script>

<style scoped>
.bb-scroll {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.bb {
    --bb-gap: 36px;
    --bb-line: rgba(255, 255, 255, 0.55);
    display: inline-block;
    min-width: 100%;
    box-sizing: border-box;
    padding: 28px 28px 32px;
    border-radius: 14px;
    background:
        radial-gradient(ellipse at 50% 0%, rgba(3, 229, 183, 0.16), transparent 60%),
        #232323;
    color: #ffffff;
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
}

.bb-head {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-bottom: 6px;
}

.bb-head__ball {
    flex: 0 0 auto;
    fill: #03e5b7;
}

.bb-head__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 34px;
    font-weight: 400;
    line-height: 1.1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background-image: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.bb-head__subtitle {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.bb--empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 24px;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 600;
    text-align: center;
}

.bb-empty__cup {
    fill: #03e5b7;
    opacity: 0.8;
}

.bb-note {
    margin: 10px auto 0;
    max-width: 520px;
    padding: 6px 12px;
    border-radius: 6px;
    background: rgba(3, 229, 183, 0.14);
    color: #8ff5df;
    font-size: 12px;
    text-align: center;
}

.bb-grid {
    display: flex;
    justify-content: center;
    gap: var(--bb-gap);
    margin-top: 22px;
}

.bb-col {
    flex: 0 0 var(--bb-card-w);
    width: var(--bb-card-w);
}

.bb-col__head {
    min-height: 44px;
    margin-bottom: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    text-align: center;
}

.bb-col--final .bb-col__head {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.bb-col__name {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.bb-col__format {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.65);
}

.bb-col--final .bb-col__format {
    color: rgba(255, 255, 255, 0.9);
}

.bb-col__body {
    display: flex;
    flex-direction: column;
}

.bb-cell {
    position: relative;
    flex: 1 1 0;
    display: flex;
    align-items: center;
}

/* connectors: drawn in the gap between two columns */
.bb-ln {
    position: absolute;
    pointer-events: none;
}

.bb-ln--out,
.bb-ln--in {
    top: 50%;
    width: calc(var(--bb-gap) / 2);
    border-top: 2px solid var(--bb-line);
}

.bb-ln--join {
    height: 50%;
    border-left: 2px solid var(--bb-line);
}

.bb-ln--join-top {
    top: 50%;
}

.bb-ln--join-bottom {
    top: 0;
    height: calc(50% + 2px);
}

.bb-col--left .bb-ln--out { left: 100%; }
.bb-col--left .bb-ln--join { left: calc(100% + var(--bb-gap) / 2); }
.bb-col--left .bb-ln--in { right: 100%; }

.bb-col--right .bb-ln--out { right: 100%; }
.bb-col--right .bb-ln--join { right: calc(100% + var(--bb-gap) / 2); border-left: 0; border-right: 2px solid var(--bb-line); }
.bb-col--right .bb-ln--in { left: 100%; }

.bb-col--final .bb-ln--in { right: 100%; }
.bb-col--final .bb-ln--in-right { right: auto; left: 100%; }

.bb-card {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 84px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    background: #1b1b1b;
}

.bb-card--final {
    border-color: #03e5b7;
    box-shadow: 0 0 0 1px rgba(3, 229, 183, 0.35), 0 10px 30px rgba(3, 229, 183, 0.18);
}

.bb-card--bye {
    opacity: 0.6;
}

.bb-team {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 31px;
    padding: 0 8px 0 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.bb-team__seed {
    flex: 0 0 16px;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
    text-align: right;
}

.bb-team__logo {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    overflow: hidden;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #ffffff;
}

.bb-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.bb-team__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
}

.bb-team--empty .bb-team__name {
    font-size: 11px;
    font-style: italic;
    color: rgba(255, 255, 255, 0.45);
}

.bb-team__adv {
    flex: 0 0 auto;
    padding: 0 4px;
    border: 1px solid #03e5b7;
    border-radius: 4px;
    color: #03e5b7;
    font-size: 9px;
    font-weight: 700;
    line-height: 14px;
}

.bb-team__score {
    flex: 0 0 26px;
    text-align: right;
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.bb-team--won .bb-team__name {
    font-weight: 700;
}

.bb-team--won .bb-team__score {
    color: #03e5b7;
}

.bb-team--lost {
    opacity: 0.5;
}

.bb-card__games {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 20px;
    padding: 0 8px;
    overflow: hidden;
    white-space: nowrap;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.55);
}

.bb-game {
    padding: 0 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.07);
    color: inherit;
    font: inherit;
    line-height: 15px;
}

.bb-game--link {
    cursor: pointer;
}

.bb-game--link:hover {
    background: #03e5b7;
    color: #1b1b1b;
}

/* phones */
.bb--narrow {
    display: block;
    padding: 20px 14px 22px;
}

.bb--narrow .bb-head__title {
    font-size: 22px;
}

.bb--narrow .bb-grid {
    display: block;
    margin-top: 10px;
}

.bb--narrow .bb-col {
    width: 100%;
}

.bb--narrow .bb-col__body {
    gap: 10px;
}

.bb--narrow .bb-cell {
    flex: none;
}

.bb-col__format--list {
    margin-bottom: 10px;
    text-align: center;
}

.bb-tabs {
    display: flex;
    gap: 6px;
    margin-top: 14px;
    overflow-x: auto;
}

.bb-tab {
    flex: 1 0 auto;
    padding: 7px 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.75);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.bb-tab--active {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #ffffff;
}

/* the cup above the final */
.bb-champion {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(50% + 54px);
    text-align: center;
}

.bb-champion--static {
    position: static;
    margin-top: 14px;
}

.bb-champion__cup {
    display: block;
    margin: 0 auto;
    fill: rgba(255, 255, 255, 0.18);
}

.bb-champion__cup--won {
    fill: #03e5b7;
    filter: drop-shadow(0 4px 14px rgba(3, 229, 183, 0.45));
}

.bb-champion__label {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
}

.bb-champion__name {
    font-size: 16px;
    font-weight: 800;
}
</style>
